import { BarChart, Bar, XAxis, YAxis, Tooltip, Text, ResponsiveContainer, TooltipProps } from 'recharts';
import Spinner from 'react-spinkit';
import {
  getSchoolChartData,
  parseSchoolChartData,
  getSchoolChartFill,
  SchoolChartDataCalculationParams,
} from '../reportUtilities';
import styles from '../DistrictReport/DistrictChart.module.scss';
import { ReportingMixpanelEvent } from '../../../peekapak-types/ReportDataExchangeTypes';
import { useEffect, useRef, useState } from 'react';
import { logDebugLevel } from '../../GlobalFunctions';

interface Props {
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPlatformEvents: ReportingMixpanelEvent[];
  newMixpanelEvents: ReportingMixpanelEvent[];
  eventToChart: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  isLoading: boolean;
  loadingStatus: string;
}

const MIN_BAR_WIDTH = 20;
const GAP_WIDTH = 5;

const SchoolChart = ({
  legacyPeekavilleEvents,
  legacyPlatformEvents,
  newMixpanelEvents,
  eventToChart,
  startDate,
  endDate,
  isLoading,
  loadingStatus,
}: Props): JSX.Element => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [parsedChartData, setParsedChartData] = useState(undefined);
  const [barKeys, setBarKeys] = useState(undefined);
  const [isParametersSet, setIsParametersSet] = useState(false);

  // ResizeObserver to monitor container width changes
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        setContainerWidth(chartContainerRef.current.getBoundingClientRect().width);
      }
    };

    // Update width on mount
    updateWidth();

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        logDebugLevel(`SchoolChart: ResizeObserver update width: ${entry.contentRect.width}`);
        setContainerWidth(entry.contentRect.width);
      }
    });

    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      if (chartContainerRef.current) {
        resizeObserver.unobserve(chartContainerRef.current);
        logDebugLevel('SchoolChart: ResizeObserver undoing');
      }
    };
  }, []);

  // Calculate optimal number of bars based on container width and bar width constraints
  useEffect(() => {
    if (containerWidth > 0 && startDate && endDate) {
      const params: SchoolChartDataCalculationParams = {
        startDate,
        endDate,
        minBarWidth: MIN_BAR_WIDTH,
        gapWidth: GAP_WIDTH,
        containerWidth,
      };

      const data = getSchoolChartData(legacyPeekavilleEvents, legacyPlatformEvents, newMixpanelEvents, params);
      const parsedChartData = data && parseSchoolChartData(data, eventToChart);
      setParsedChartData(parsedChartData);
      const barKeys =
        parsedChartData &&
        Object.keys(parsedChartData[0]).filter((key) => key !== 'name' && key !== 'fromDate' && key !== 'toDate');
      setBarKeys(barKeys);
      setIsParametersSet(true);
      logDebugLevel(`SchoolChart: Got data for width: ${containerWidth}`);
    } else {
      setIsParametersSet(false);
    }
  }, [
    containerWidth,
    eventToChart,
    newMixpanelEvents,
    legacyPeekavilleEvents,
    legacyPlatformEvents,
    startDate,
    endDate,
  ]);

  logDebugLevel('isParametersSet: ', isParametersSet);
  logDebugLevel('parsedChartData: ', parsedChartData);
  return (
    <div ref={chartContainerRef} className={styles.schoolContainer}>
      {isParametersSet && (
        <ResponsiveContainer width='100%' height={500}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <div>
                <p>{loadingStatus}</p>
              </div>
              <Spinner />
            </div>
          ) : (
            <BarChart width={900} height={300} data={parsedChartData}>
              <XAxis dataKey='name' interval={0} tick={<CustomXAxisTick />} height={45} />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              {barKeys &&
                barKeys.map((key, index) => (
                  <Bar dataKey={key} key={key} stackId='a' fill={getSchoolChartFill(index)} barSize={70}></Bar>
                ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      )}
      <h3 className={styles.chartLabel}>Period beginning:</h3>
    </div>
  );
};

interface TickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
}
const CustomXAxisTick = ({ x, y, payload }: TickProps) => {
  if (payload && payload.value) {
    return (
      <Text fontSize={'14px'} width={75} x={x} y={y} textAnchor='middle' verticalAnchor='start'>
        {payload.value}
      </Text>
    );
  }
  return null;
};

interface CustomTooltipProps extends TooltipProps<number, string> {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    dataKey: string;
    color?: string;
    fill?: string;
    payload: {
      name: string;
      fromDate: number;
      toDate: number;
      Educators?: number;
      Students?: number;
    };
  }>;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload;
    const fromDate = new Date(data.fromDate);
    // toDate is stored as 23:59:59.999 of the last day, so we display it as that day
    // But since it's nearly midnight, we just use the date as-is for display
    const toDate = new Date(data.toDate);

    const formatDateWithMonth = (date: Date) => {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    };

    const formatDateDayOnly = (date: Date) => {
      return date.toLocaleString('en-US', {
        day: 'numeric',
      });
    };

    // Check if the range spans different years
    const sameYear = fromDate.getFullYear() === toDate.getFullYear();
    const sameMonth = sameYear && fromDate.getMonth() === toDate.getMonth();

    let dateRange: string;
    if (!sameYear) {
      // Different years: "Dec 29 2024 - Jan 5 2025"
      dateRange = `${formatDateWithMonth(fromDate)} ${fromDate.getFullYear()} - ${formatDateWithMonth(toDate)} ${toDate.getFullYear()}`;
    } else if (!sameMonth) {
      // Same year, different months: "Oct 29 - Nov 5"
      dateRange = `${formatDateWithMonth(fromDate)} - ${formatDateWithMonth(toDate)}`;
    } else {
      // Same month: "Nov 14 - 21"
      dateRange = `${formatDateWithMonth(fromDate)} - ${formatDateDayOnly(toDate)}`;
    }

    return (
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          padding: '10px',
          borderRadius: '4px',
        }}
      >
        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>{dateRange}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ margin: '4px 0', color: entry.fill || entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }

  return null;
};

export default SchoolChart;
